import React from 'react';
import { RouteComponentProps } from 'react-router';
import { func, types, cartFx, axius } from 'utils';
import { Button } from 'react-bootstrap';
import { CustomRadio, IonLoading, Touchable } from 'components';
import { Input } from 'antd';
import { _store, _update } from '_Store';
import { useIonAlert, useIonRouter, useIonToast } from '@ionic/react';
import _ from 'lodash';

interface StateProps {
	minCartValue: boolean;

	checkout: (deliveryFee: number) => void;
}
interface Props extends RouteComponentProps, types.StateProps, StateProps {}
const Checkout: React.FC<Props> = (props) => {
	const [presentAlert] = useIonAlert();
	const [presentToast] = useIonToast();
	const router = useIonRouter();

	const {
		__data,
		__data: { cart, location, deliveryFees, promotions },
		__auth: { user },
	} = _store.useState();

	const { checkout, minCartValue } = props;
	const [loading, setLoading] = React.useState('');

	const [showForm, setShowForm] = React.useState({
		deliveryOnstructions: false,
	});
	const [isPromo, setPromoOpen] = React.useState(
		Boolean(cart.voucher) || false
	);
	const [promoCode, setPromoCode] = React.useState(cart.voucher || '');

	React.useEffect(() => {
		if (cart.voucher) {
			applyPromoCode(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cart]);

	const applyPromoCode = (e: any) => {
		e && e.preventDefault();
		if (!hasPromoItems) {
			e && setLoading(`applying voucher...`);
			if (promoCode.trim() !== '') {
				axius
					.post('vouchers/apply', {
						code: promoCode.trim(),
						items: cart.items,
						storeCode: cart.store.code,
					})
					.then((res) => {
						if (res.status === 201) {
							if (cart.voucher && cart.voucher !== promoCode) {
								presentAlert(
									`Only one voucher can be used per order. [${promoCode}] has replaced [${cart.voucher}].`
								);
							}
							_update.data('cart', {
								...cart,
								voucher: promoCode,
								discount: res.discount,
							});
							e && presentToast(res.message, 1500);
						} else {
							removeVoucher();
							presentAlert(res.message || 'Error');
						}
						setLoading(``);
					});
			}
		} else {
			presentAlert(`Vouchers cannot be combined with promotional items`);
		}
	};

	const removeVoucher = () => {
		_update.data('cart', {
			...cart,
			voucher: '',
			discount: 0,
		});
		setPromoCode('');
	};

	const discount =
		(cart.discount || 0) +
		cart.items.reduce((a, b) => a + (b.discount || 0), 0);
	const cartTotal = cartFx.total(cart.items);

	const total = {
		cart: cartTotal,
		discount,
		delivery: +(cart.deliveryFee || 0),
		total: discount - (cartTotal + +(cart.deliveryFee || 0)),
	};

	const activeStore = cart.store;

	const hasPromoItems = Boolean(
		cart.promotions.length + cart.items.filter((item) => item.promotion).length
	);

	let paymentOptions = __data.paymentOptions;
	if (+user.wallet > 0) {
		paymentOptions = [
			...paymentOptions,
			{
				code: 'WALLET',
				label: `Wallet: ₦${func.numberFormat(user.wallet)}`,
				disabled: +user.wallet < total.total,
			},
		];
	}

	const cashbackPromos = cartFx
		.myPromos({ promotions, user })
		.filter((p) => p.type === types.PromotionTypes.CASHBACK);

	const cartHasPromoItems = Boolean(
		_.compact(
			cart.items.map((item) => cartFx.myPromos({ promotions, item, user })[0])
		).length
	);

	return (
		<React.Fragment>
			<IonLoading
				open={loading ? true : false}
				message={loading}
				onCancel={() => setLoading('')}
			/>

			<div id="Checkout" className="p-3 content c-small">
				<div className="d-flex align-items-center mb-3">
					<i className="fa fa-store text-muteds primary" />
					<div className="ml-2">{func.venueName(cart.store)}</div>
				</div>

				{location.option === types.OrderTypes.DELIVERY && (
					<>
						<div className="d-flex align-items-center mb-3">
							<i className="fa fa-home text-muteds primary" />
							<div className="ml-2">{location.address.name}</div>
						</div>
					</>
				)}

				{location.option === types.OrderTypes.DELIVERY &&
					deliveryFees?.length > 0 && (
						<div>
							<div className="pb-4s mt-4">
								<div className="primarys fw-600s mb-2 mt-4 small">
									Delivery Partners
								</div>
								<div className="side py-2">
									{deliveryFees.map((df, i) => (
										<div key={`${i}_${df.partner}`} className="mx-3 my-2s">
											<CustomRadio
												key={`${i}_${df.partner}`}
												label={
													<div className="my-2">
														<div className="d-flex align-items-center justify-content-between fw-600s">
															<div>{func.ucWords(df.partner)}</div>
															<div>₦{func.numberFormat(df.amount)}</div>
														</div>
													</div>
												}
												selected={cart.delivery.channel === df.partner}
												className="primarys"
												onClick={() => {
													const copyCart: types.Cart = func.copyObject(cart);
													copyCart['deliveryFee'] = df.amount;
													copyCart['delivery']['channel'] = df.partner;
													_update.data('cart', { ...copyCart });
												}}
											/>
										</div>
									))}
								</div>
							</div>
							<div className="dividers" />
						</div>
					)}

				{cart.store?.paymentOptions?.length > 0 && (
					<div>
						<div className="pb-4s mt-4">
							<div className="primarys fw-600s mb-2 mt-4 small">
								Payment Options
							</div>
							<div className="side py-2">
								{paymentOptions
									.filter((po) => cart.store?.paymentOptions.includes(po.code))
									.map((po: any) => {
										const cashbackPromo = cashbackPromos.find((cp) =>
											cp.gateways.includes(po.code)
										);
										const isOrderMinimum =
											cashbackPromo &&
											+cashbackPromo.limit.minimumOrder <= total.cart;
										return (
											<div key={po.code} className="mx-3 my-3s">
												<CustomRadio
													key={po.code}
													label={
														<div>
															{po.label.replace('delivery', location.option)}
															{isOrderMinimum && cashbackPromo && (
																<div className="mt-1 small text-muted">
																	<i className={`fa fa-gift primary`} />{' '}
																	{cashbackPromo.title}
																</div>
															)}
														</div>
													}
													selected={cart.paymentType === po.code ? true : false}
													disabled={po.disabled}
													className="primarys"
													onClick={() => {
														_update.data('cart', {
															...cart,
															paymentType: po.code,
															promotions:
																isOrderMinimum && cashbackPromo
																	? [...cart.promotions, cashbackPromo.id]
																	: cart.promotions,
														});
													}}
												/>
											</div>
										);
									})}
							</div>
						</div>
						<div className="dividers" />
					</div>
				)}

				<div className="my-3 side p-2 mt-3">
					<Touchable onClick={() => setPromoOpen(!isPromo)}>
						<div className="text-center primary font-weight-bold pointer">
							<i className="fa fa-gift primary mr-1"></i> Use Promo Code
						</div>
					</Touchable>
					{isPromo && (
						<div className="mt-2">
							{/*<label className="font-weight-bold">Enjoy amazing discount{" "}<i className={"fa fa-gift primary"}></i></label>*/}
							<form className="d-flex" onSubmit={(e) => applyPromoCode(e)}>
								<input
									type="text"
									className="form-control mr-2"
									placeholder="Enter promo code"
									value={promoCode}
									disabled={hasPromoItems}
									onChange={(e) =>
										setPromoCode(e.target.value.replace(' ', ''))
									}
								/>
								<button
									type="submit"
									className="btn btn-primary"
									style={{ height: '34px' }}
									disabled={promoCode.trim() === '' || hasPromoItems}
								>
									Redeem
								</button>
							</form>
							{cart.voucher && (
								<div
									className="text-danger text-center fw-600 small mt-1"
									onClick={removeVoucher}
								>
									<i className="fa fa-times" /> Remove voucher
								</div>
							)}
							{(hasPromoItems || (promoCode.trim() && cartHasPromoItems)) && (
								<div className="text-danger small mt-1">
									<i className="fa fa-exclamation-circle" /> Vouchers cannot be
									combined with promotional items
								</div>
							)}
						</div>
					)}
				</div>

				<div className="divider my-3" />
				<div>
					<div className="primarys d-flex justify-content-between fw-600s mb-2 mt-4s">
						<div>Meal:</div>
						<div className="fw-500">
							₦{func.numberFormat(cartFx.total(cart.items))}
						</div>
					</div>
					<div className="divider my-2 hide" />

					{total.discount > 0 && (
						<div>
							<div className="primarys d-flex justify-content-between mb-2">
								<div>Discount:</div>
								<div className="fw-500">
									- ₦{func.numberFormat(total.discount)}
								</div>
							</div>
							<div className="divider my-2 hide" />
						</div>
					)}

					{location.option === types.OrderTypes.DELIVERY && (
						<div>
							<div className="primarys d-flex justify-content-between mb-2">
								<div>Delivery Fee:</div>
								<div className="fw-500">
									₦{func.numberFormat(total.delivery)}
								</div>
							</div>
							<div className="divider my-3 hide" />
						</div>
					)}

					<div className="dividersss my-3" />
					<div className="primarys d-flex justify-content-between my-4- fw-600">
						<div>Total:</div>
						<div className="fs-20 primary">
							₦{func.numberFormat(total.total)}
						</div>
					</div>
				</div>

				{location.option === types.OrderTypes.DELIVERY && (
					<>
						<div className="divider my-3" />
						<div
							className="d-flex justify-content-between align-items-center mt-4"
							onClick={() =>
								setShowForm((ssf) => ({
									...ssf,
									deliveryOnstructions: !ssf.deliveryOnstructions,
								}))
							}
						>
							<div className="primarys fw-600smb-2">
								Leave a message for the store
							</div>
							<div className="small text-muted">
								<i
									className={`fa fa-chevron-${showForm.deliveryOnstructions ? 'down' : 'right'}`}
								/>
							</div>
						</div>
						{showForm.deliveryOnstructions && (
							<div className="mt-3">
								<Input.TextArea
									size="large"
									rows={3}
									value={cart.delivery.instructions}
									placeholder="Let the rider know if you have any special requests"
									onChange={(e) => {
										_update.data('cart', {
											...cart,
											delivery: {
												...cart.delivery,
												instructions: e.target.value,
											},
										});
									}}
								/>
							</div>
						)}
						<div className="divider my-3" />
					</>
				)}

				<div className="footers pb-3 mt-5">
					<Button
						variant={`${minCartValue ? 'secondary' : 'gray'} btn-block btn-lg text-uppercases`}
						// disabled={
						// 	!!(
						// 		location.option === types.OrderTypes.DELIVERY && !total.delivery
						// 	)
						// }
						onClick={() => checkout(total.delivery)}
					>
						<div className="d-flex justify-content-between">
							<div>Place Order</div>
							<div>
								<b>₦{func.numberFormat(total.total)}</b>
							</div>
						</div>
					</Button>
				</div>

				<div className="text-center my-2">
					<span
						className="primary fw-600 ins"
						onClick={() => router.push(`/store/${activeStore.code}`)}
					>
						Continue Shopping
					</span>
				</div>
			</div>

			{/*<RepDashTnC*/}
			{/*	open={tnc.modal}*/}
			{/*	onAccept={() => setTnc((st) => ({ ...st, accepted: true }))}*/}
			{/*	onCancel={() => setTnc((st) => ({ ...st, modal: false }))}*/}
			{/*/>*/}
		</React.Fragment>
	);
};

export default Checkout;
